'use client';

import { CaretUp, InfoIcon, PlusTrans } from 'assets/icons';
import {
  ReactNode,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { CustomImage as Image } from '../..';
import { OffersModalNewContext } from 'components/shared/OffersModal';
import styles from './OffersCarousel.module.css';

export type OffersDataType = {
  image?: string[];
  imageType?: 'circle' | 'square';
  title: ReactNode;
  onClick?: () => void;
  modal?: ReactNode;
  // Optional: overrides default content height (56px). Accepts number (px) or string (e.g., '64px')
  contentHeight?: number | string;
};

export const OffersCarousel = ({
  className,
  data,
  elementBgColor,
  leftAligned = false,
  $isRarePage = false,
}: {
  className?: string;
  data: OffersDataType[];
  elementBgColor?: string;
  leftAligned?: boolean;
  $isRarePage?: boolean;
}) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const autoplayPlugin = useRef(
      Autoplay({ delay: 4000, stopOnInteraction: false }),
    );
    const [emblaRef, emblaApi] = useEmblaCarousel(
      {
        align: leftAligned ? 'start' : 'center',
        slidesToScroll: 1,
        skipSnaps: false,
        loop: true,
      },
      [autoplayPlugin.current],
    );

    // const RenderOfferElement = (props: OffersDataType) => {
    // }

    const OffersModalNewClient = useContext(OffersModalNewContext);

    const handleImageModal = ({ modal }: { modal?: ReactNode }) => {
      // va.track("sleep contributor modal - CLICK");
      OffersModalNewClient.set(modal);
      OffersModalNewClient.show();
    };

    const elementColor = elementBgColor || '#f5f5f5'; // Default background color for offer elements

    // Calculate the maximum height from all offers to ensure consistent card heights
    const maxContentHeight = useMemo(() => {
      const defaultHeight = 56; // Default height in pixels
      const heights = data.map((offer) => {
        if (typeof offer.contentHeight === 'number') return offer.contentHeight;
        if (typeof offer.contentHeight === 'string') {
          const parsed = parseInt(offer.contentHeight.replace('px', ''));
          return isNaN(parsed) ? defaultHeight : parsed;
        }
        return defaultHeight;
      });
      return (heights.length ? Math.max(...heights) : defaultHeight) + 'px';
    }, [data]);

    const renderOfferElement = (
      offer: OffersDataType,
      elementColor: string,
      index?: number,
    ) => {
      return (
        <div
          className={styles.offerElement}
          key={index}
          onClick={
            offer.onClick ??
            (offer.modal
              ? () => handleImageModal({ modal: offer.modal })
              : undefined)
          }
          style={
            {
              '--element-color': elementColor,
              '--content-height': maxContentHeight,
              cursor: offer.onClick ? 'pointer' : undefined,
            } as React.CSSProperties
          }
        >
          <div className="content">
            {offer.image && offer.image.length > 0 ? (
              <div
                className={`image-container ${
                  offer.imageType === 'square' ? 'square' : ''
                }`}
              >
                {offer.image.map((img, index) => (
                  <Image src={img} alt="" height="24" width="24" key={index} />
                ))}
              </div>
            ) : null}
            <p>{offer.title}</p>
            {offer.onClick ? (
              <InfoIcon fill="rgba(0,0,0,0.3)" style={{ cursor: 'pointer' }} />
            ) : null}
            {offer.modal ? (
              <PlusTrans className="plus" style={{ cursor: 'pointer' }} />
            ) : null}
          </div>
        </div>
      );
    };

    const scrollNext = useCallback(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);

    useEffect(() => {
      if (emblaApi) {
        const autoplay = emblaApi.plugins().autoplay as any;
        if (autoplay) {
          const onPointerDown = () => autoplay.stop();
          const onPointerUp = () => autoplay.play();

          emblaApi.on('pointerDown', onPointerDown);
          emblaApi.on('pointerUp', onPointerUp);

          return () => {
            emblaApi.off('pointerDown', onPointerDown);
            emblaApi.off('pointerUp', onPointerUp);
          };
        }
      }
    }, [emblaApi]);

    useEffect(() => {
      const root = rootRef.current;
      if (!root || !emblaApi) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            autoplayPlugin.current.play();
          } else {
            autoplayPlugin.current.stop();
          }
        },
        { threshold: 0 },
      );
      observer.observe(root);
      return () => observer.disconnect();
    }, [emblaApi]);

    if (data.length < 1) return null;

    if (data.length === 1) return renderOfferElement(data[0], elementColor, 0);

    return (
      <div
        className={className ? `${styles.root} ${className}` : styles.root}
        ref={rootRef}
        data-left-aligned={leftAligned ? 'true' : undefined}
        data-rare={$isRarePage ? 'true' : undefined}
      >
        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {data.map((offer, index) => (
              <div className="embla__slide" key={index}>
                {renderOfferElement(offer, elementColor, index)}
              </div>
            ))}
          </div>
        </div>
        <button
          className="next-btn"
          onClick={scrollNext}
          aria-label="Next"
          title="Next"
        >
          <CaretUp
            fill="#ffffff"
            style={{
              transform: 'rotate(90deg)',
              width: '12px',
              height: 'auto',
            }}
          />
        </button>
      </div>
    );
};
